import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Phone, MessageSquare, Globe, AlertCircle, Send } from "lucide-react";
import { COUNTRY_LIST } from "../lib/countries";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";

export default function SupportPage() {
  const { t, i18n } = useTranslation();
  const [country, setCountry] = useState("US");
  const [resources, setResources] = useState({ resources: [], global_resources: [] });
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [sending, setSending] = useState(false);

  const faqs = t("support.faqs", { returnObjects: true }) || [];

  useEffect(() => {
    if (!country) return;
    API.get(`/support/resources/${country}`).then(({ data }) => setResources(data));
  }, [country]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error(t("common.required_fields"));
    setSending(true);
    try {
      await API.post("/support/contact", { ...form, language: i18n.language });
      toast.success(t("support.submitted"));
      setForm({ name: "", email: "", topic: "general", message: "" });
    } catch (e) {
      toast.error(errorMessage(e, t("support.could_not_send")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-support">
      <Section eyebrow={t("support.eyebrow")} title={t("support.title")} subtitle={t("support.subtitle")}>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-surface p-6">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">{t("support.faq")}</div>
            <Accordion type="single" collapsible data-testid="support-faq">
              {(Array.isArray(faqs) ? faqs : []).map((f, i) => (
                <AccordionItem value={`item-${i}`} key={i}>
                  <AccordionTrigger className="text-start text-white" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/70">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="card-surface p-6">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">{t("support.contact")}</div>
            <form onSubmit={submit} className="grid gap-3" data-testid="contact-form">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("support.your_name")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="contact-name" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("support.your_email")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="contact-email" />
              <input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder={t("support.topic")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="contact-topic" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("support.message")} rows={5} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="contact-message" />
              <button type="submit" disabled={sending} className="btn-primary text-sm inline-flex items-center gap-2 w-fit" data-testid="contact-submit"><Send className="w-4 h-4" /> {t("support.submit_contact")}</button>
            </form>
          </div>
        </div>
      </Section>

      <Section eyebrow={t("support.crisis_eyebrow")} title={t("support.mh_title")} subtitle={t("support.mh_sub")}>
        <div className="flex items-center gap-3 mb-5">
          <Globe className="w-4 h-4 gold-text" />
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="bg-[#0F1116] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="support-country">
            {COUNTRY_LIST.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-4" data-testid="support-resources">
          {(resources.resources || []).map((r, i) => (
            <div key={i} className="card-surface p-5">
              <div className="flex items-center gap-2"><AlertCircle className="w-4 h-4 gold-text" /><div className="font-display text-lg">{r.name}</div></div>
              <div className="mt-3 grid gap-1 text-sm text-white/80">
                {r.phone && <div className="flex gap-2 items-center"><Phone className="w-3.5 h-3.5 gold-text" /> {r.phone}</div>}
                {r.sms && <div className="flex gap-2 items-center"><MessageSquare className="w-3.5 h-3.5 gold-text" /> {r.sms}</div>}
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="flex gap-2 items-center hover:underline"><Globe className="w-3.5 h-3.5 gold-text" /> {r.url}</a>}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">{t("support.global_resources_label")}</div>
          <div className="grid md:grid-cols-2 gap-4">
            {(resources.global_resources || []).map((r, i) => (
              <div key={i} className="card-surface p-5">
                <div className="font-display text-lg">{r.name}</div>
                <div className="text-white/70 text-sm mt-1">{r.note}</div>
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-sm text-white/85 mt-2 underline">{r.url}</a>}
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
